// src/pages/AddNewCompany.jsx
import React, { useState } from "react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Box,
  Button,
  Grid,
  TextField,
  IconButton,
  Typography,
  CircularProgress,
} from "@mui/material";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate } from "react-router-dom";
import "../styles/AddCompany.css";
import CustomCard from "../../../common/CustomCard";
import { addCompany, createCompany } from "../services/api";
import PageHeader from "../../../common/PageHeader";

const validationSchema = Yup.object().shape({
  name: Yup.string().required("Company Name is required"),
  industry: Yup.string().required("Industry is required"),
});

const AddNewCompany = ({ handleAddCompany }) => {
  const navigate = useNavigate();
  const [apiError, setApiError] = useState("");
  const [loading, setLoading] = useState(false);

  const initialValues = {
    name: "",
    industry: "",
    revenue: "",
    employees: "",
    location: "",
  };

  return (
    <Box className="padding-20">
      {/* Header */}
       <PageHeader
        title="Add New Company"
        description="Add company, unlock insights"
      />
      <CustomCard>
         {loading && (
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          backgroundColor: "rgba(255, 255, 255, 0.7)",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          zIndex: 10,
        }}
      >
        <CircularProgress />
      </Box>
    )}
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={async (values, { resetForm }) => {
            try {
              setLoading(true);
              const response = await createCompany(values);

              if (response.success) {
                toast.success("Company created successfully!");
                resetForm();
                navigate("/due-diligence");
              } else {
                toast.error(response.error?.message || "Something went wrong");
              }
            } catch (err) {
              toast.error(err.error?.message || "Failed to create company");
            } finally {
              setLoading(false); 
            }
          }}
        >
          {() => (
            <Form>
              <Grid container spacing={2}>
                {/* Row 1 */}
                <Grid item xs={12} md={3}>
                  <label>
                    Company Name <span className="required">*</span>
                  </label>
                  <Field
                    as={TextField}
                    name="name"
                    placeholder="Enter company name"
                    fullWidth
                    size="small"
                    variant="outlined"
                  />
                  <ErrorMessage
                    name="name"
                    component="div"
                    className="error-msg"
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <label>
                    Industry <span className="required">*</span>
                  </label>
                  <Field
                    as={TextField}
                    name="industry"
                    placeholder="E.g., Software, Manufacturing, Healthcare"
                    fullWidth
                    size="small"
                    variant="outlined"
                  />
                  <ErrorMessage
                    name="industry"
                    component="div"
                    className="error-msg"
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <label>Revenue</label>
                  <Field
                    as={TextField}
                    name="revenue"
                    placeholder="e.g., $10M ARR"
                    fullWidth
                    size="small"
                    variant="outlined"
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <label>Employees</label>
                  <Field
                    as={TextField}
                    name="employees"
                    placeholder="e.g., 150"
                    fullWidth
                    size="small"
                    variant="outlined"
                  />
                </Grid>

                {/* Row 2 */}
                <Grid item xs={12} md={3}>
                  <label>Location</label>
                  <Field
                    as={TextField}
                    name="location"
                    placeholder="e.g., San Francisco, CA"
                    fullWidth
                    size="small"
                    variant="outlined"
                  />
                </Grid>
              </Grid>

              {/* Footer Buttons */}
              <Box mt={5} display="flex" gap={2} justifyContent="flex-end">
                <Button variant="outlined" onClick={() => navigate(-1)}>
                  Cancel
                </Button>

                <Button
                  type="submit"
                  variant="contained"
                  startIcon={<AddIcon />}
                >
                  Add Company
                </Button>
              </Box>
            </Form>
          )}
        </Formik>
      </CustomCard>
    </Box>
  );
};

export default AddNewCompany;
